import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Gift, Plus, Users, User, Percent, Film, Tag, Trash2, Power, PowerOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminOffersPage() {
  const { token } = useAuth();
  const [offers, setOffers] = useState([]);
  const [offerTypes, setOfferTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOffer, setNewOffer] = useState({
    target_type: 'all',
    target_email: '',
    offer_type: 'discount',
    value: '',
    description: ''
  });

  useEffect(() => {
    if (token) {
      fetchOffers();
      fetchOfferTypes();
    }
  }, [token]);

  const fetchOffers = async () => {
    try {
      // Use the token from localStorage directly as a fallback
      const authToken = token || localStorage.getItem('token');
      if (!authToken) {
        console.log('No token available yet');
        return;
      }
      const res = await axios.get(`${API}/api/admin/offers`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setOffers(res.data.offers || []);
    } catch (err) {
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferTypes = async () => {
    try {
      const authToken = token || localStorage.getItem('token');
      if (!authToken) return;
      const res = await axios.get(`${API}/api/admin/offers/types`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setOfferTypes(res.data);
    } catch (err) {
      console.error('Failed to load offer types');
    }
  };

  const createOffer = async () => {
    if (!newOffer.value || !newOffer.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newOffer.target_type === 'specific' && !newOffer.target_email) {
      toast.error('Please enter a target email for specific user offers');
      return;
    }

    try {
      const authToken = token || localStorage.getItem('token');
      await axios.post(`${API}/api/admin/offers`, newOffer, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Offer created successfully!');
      setShowCreateModal(false);
      setNewOffer({
        target_type: 'all',
        target_email: '',
        offer_type: 'discount',
        value: '',
        description: ''
      });
      fetchOffers();
    } catch (err) {
      toast.error('Failed to create offer');
    }
  };

  const toggleOffer = async (offerId, isActive) => {
    try {
      const authToken = token || localStorage.getItem('token');
      const endpoint = isActive ? 'deactivate' : 'activate';
      await axios.patch(`${API}/api/admin/offers/${offerId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success(`Offer ${isActive ? 'deactivated' : 'activated'}`);
      fetchOffers();
    } catch (err) {
      toast.error('Failed to update offer');
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    
    try {
      const authToken = token || localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success('Offer deleted');
      fetchOffers();
    } catch (err) {
      toast.error('Failed to delete offer');
    }
  };

  const getOfferTypeIcon = (type) => {
    switch (type) {
      case 'discount': return <Percent className="w-4 h-4" />;
      case 'free_upgrade': return <Tag className="w-4 h-4" />;
      case 'bonus_videos': return <Film className="w-4 h-4" />;
      case 'free_trial': return <Gift className="w-4 h-4" />;
      default: return <Gift className="w-4 h-4" />;
    }
  };

  const getTargetIcon = (type) => {
    switch (type) {
      case 'specific': return <User className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6" data-testid="admin-offers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink-950 flex items-center gap-3">
            <Gift className="w-7 h-7 text-amber-400" />
            Special Offers
          </h1>
          <p className="text-ink-500 mt-1">
            Create and manage promotional offers for users
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Offer
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-ink-50 rounded-xl p-4 border border-ink-200/50">
          <div className="text-ink-500 text-sm">Total Offers</div>
          <div className="text-2xl font-bold text-ink-950 mt-1">{offers.length}</div>
        </div>
        <div className="bg-ink-50 rounded-xl p-4 border border-ink-200/50">
          <div className="text-ink-500 text-sm">Active Offers</div>
          <div className="text-2xl font-bold text-green-400 mt-1">
            {offers.filter(o => o.is_active).length}
          </div>
        </div>
        <div className="bg-ink-50 rounded-xl p-4 border border-ink-200/50">
          <div className="text-ink-500 text-sm">Inactive Offers</div>
          <div className="text-2xl font-bold text-ink-500 mt-1">
            {offers.filter(o => !o.is_active).length}
          </div>
        </div>
      </div>

      {/* Offers List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-20 bg-ink-100/30 rounded-xl border border-ink-200/50">
          <Gift className="w-16 h-16 mx-auto mb-4 text-ink-500" />
          <h2 className="text-xl font-semibold text-ink-950 mb-2">No Offers Yet</h2>
          <p className="text-ink-500 mb-6">Create your first promotional offer</p>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-600 hover:bg-amber-500"
          >
            Create Offer
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <div 
              key={offer.id}
              className={`bg-ink-50 rounded-xl p-5 border transition-all ${
                offer.is_active ? 'border-amber-500/30' : 'border-ink-200/50 opacity-60'
              }`}
              data-testid={`offer-card-${offer.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      offer.is_active ? 'bg-amber-500/20 text-amber-400' : 'bg-ink-200 text-ink-500'
                    }`}>
                      {getOfferTypeIcon(offer.offer_type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink-950">{offer.description}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          offer.is_active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-ink-200 text-ink-500'
                        }`}>
                          {offer.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs bg-coral-500/20 text-coral-500">
                          {offer.offer_type.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                      <span className="text-ink-500">Target</span>
                      <div className="flex items-center gap-1 text-ink-700 mt-1">
                        {getTargetIcon(offer.target_type)}
                        {offer.target_type === 'specific' ? offer.target_email : offer.target_type.replace('_', ' ')}
                      </div>
                    </div>
                    <div>
                      <span className="text-ink-500">Value</span>
                      <div className="text-ink-700 mt-1 font-medium">
                        {offer.offer_type === 'discount' ? `${offer.value}%` : offer.value}
                      </div>
                    </div>
                    <div>
                      <span className="text-ink-500">Created</span>
                      <div className="text-ink-700 mt-1">{formatDate(offer.created_at)}</div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className={`border-ink-200 ${offer.is_active ? 'hover:bg-red-900/20 hover:border-red-700' : 'hover:bg-green-900/20 hover:border-green-700'}`}
                    onClick={() => toggleOffer(offer.id, offer.is_active)}
                  >
                    {offer.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-ink-200 hover:bg-red-900/20 hover:border-red-700 text-red-400"
                    onClick={() => deleteOffer(offer.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Offer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setShowCreateModal(false)}>
          <div 
            className="bg-white rounded-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-ink-950 mb-6 flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" />
              Create Special Offer
            </h2>

            <div className="space-y-4">
              {/* Target Type */}
              <div>
                <label className="text-sm text-ink-500 mb-2 block">Target Audience</label>
                <div className="grid grid-cols-2 gap-2">
                  {offerTypes.target_types?.map((target) => (
                    <button
                      key={target.id}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        newOffer.target_type === target.id 
                          ? 'border-amber-500 bg-amber-500/10' 
                          : 'border-ink-200 hover:border-ink-300'
                      }`}
                      onClick={() => setNewOffer({ ...newOffer, target_type: target.id })}
                    >
                      <div className="text-ink-950 font-medium text-sm">{target.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Email (for specific user) */}
              {newOffer.target_type === 'specific' && (
                <div>
                  <label className="text-sm text-ink-500 mb-2 block">User Email</label>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={newOffer.target_email}
                    onChange={(e) => setNewOffer({ ...newOffer, target_email: e.target.value })}
                    className="bg-ink-100 border-ink-200"
                  />
                </div>
              )}

              {/* Offer Type */}
              <div>
                <label className="text-sm text-ink-500 mb-2 block">Offer Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {offerTypes.offer_types?.map((type) => (
                    <button
                      key={type.id}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        newOffer.offer_type === type.id 
                          ? 'border-violet-500 bg-coral-500/10' 
                          : 'border-ink-200 hover:border-ink-300'
                      }`}
                      onClick={() => setNewOffer({ ...newOffer, offer_type: type.id })}
                    >
                      <div className="flex items-center gap-2 text-ink-950 font-medium text-sm">
                        {getOfferTypeIcon(type.id)}
                        {type.name}
                      </div>
                      <div className="text-xs text-ink-500 mt-1">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Value */}
              <div>
                <label className="text-sm text-ink-500 mb-2 block">
                  Value {newOffer.offer_type === 'discount' && '(percentage)'}
                  {newOffer.offer_type === 'bonus_videos' && '(number of videos)'}
                  {(newOffer.offer_type === 'free_upgrade' || newOffer.offer_type === 'free_trial') && '(tier: premium, platinum)'}
                </label>
                <Input
                  placeholder={
                    newOffer.offer_type === 'discount' ? '20' :
                    newOffer.offer_type === 'bonus_videos' ? '10' :
                    newOffer.offer_type === 'free_upgrade' ? 'platinum' : 'Enter value'
                  }
                  value={newOffer.value}
                  onChange={(e) => setNewOffer({ ...newOffer, value: e.target.value })}
                  className="bg-ink-100 border-ink-200"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm text-ink-500 mb-2 block">Description</label>
                <Input
                  placeholder="e.g., Black Friday 20% Off"
                  value={newOffer.description}
                  onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
                  className="bg-ink-100 border-ink-200"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 border-ink-200"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600"
                  onClick={createOffer}
                >
                  Create Offer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
